(() => {
  if (customElements.get('ordessa-mobile-gallery')) return;

  customElements.define(
    'ordessa-mobile-gallery',
    class extends HTMLElement {
      connectedCallback() {
        customElements.whenDefined('product-modal').then(() => {
          if (!this.isConnected || this.abortController) return;
          this.setup();
        });
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
        this.modalObserver?.disconnect();
        this.mediaObserver?.disconnect();
        this.resizeObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
        this.clear();
      }

      setup() {
        this.modal = this.closest('product-modal');
        this.content = this.modal?.querySelector('.product-media-modal__content');
        this.button = this.querySelector('.ordessa-mobile-gallery__zoom');
        if (!this.modal || !this.content || !this.button) return;

        this.abortController = new AbortController();
        const options = { signal: this.abortController.signal };
        this.mobile = window.matchMedia('(max-width: 749px)');

        this.button.addEventListener('pointerup', (event) => event.stopPropagation(), options);
        this.button.addEventListener('click', () => {
          if (!this.image || !this.mobile.matches || !this.modal.hasAttribute('open')) return;
          this.zoomed = !this.zoomed;
          this.render(true);
        }, options);

        this.content.addEventListener('pointerup', (event) => {
          if (this.mobile.matches && this.image && event.target === this.image) event.stopPropagation();
        }, options);
        this.content.addEventListener('load', (event) => {
          if (event.target === this.image) this.render(false);
        }, { ...options, capture: true });

        this.mobile.addEventListener('change', () => this.sync(true), options);
        window.addEventListener('resize', () => this.scheduleFit(), options);

        this.modalObserver = new MutationObserver(() => this.sync(true));
        this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
        this.mediaObserver = new MutationObserver(() => this.sync(false));
        this.mediaObserver.observe(this.content, {
          attributes: true,
          attributeFilter: ['class'],
          childList: true,
          subtree: true,
        });
        this.resizeObserver = new ResizeObserver(() => this.scheduleFit());
        this.resizeObserver.observe(this.modal.querySelector('.product-media-modal__dialog'));
        this.sync(true);
      }

      scheduleFit() {
        if (!this.image || !this.modal?.hasAttribute('open') || this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync(true);
        });
      }

      sync(reset) {
        const image = this.content.querySelector(':scope > img.active[data-media-id]');
        if (!this.mobile.matches || !this.modal.hasAttribute('open') || !image) {
          this.clear();
          return;
        }

        const changed = image !== this.image;
        this.image = image;
        if (reset || changed) this.zoomed = false;
        this.hidden = false;
        this.render(reset || changed);
      }

      render(resetScroll) {
        if (!this.image || !this.mobile.matches || !this.modal.hasAttribute('open')) return;

        this.modal.dataset.ordessaImageMode = this.zoomed ? 'zoom' : 'fit';
        const style = window.getComputedStyle(this.content);
        const availableWidth = this.content.clientWidth - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0);
        const availableHeight = this.content.clientHeight - parseFloat(style.paddingTop || 0) - parseFloat(style.paddingBottom || 0);
        const imageWidth = this.image.naturalWidth || Number(this.image.getAttribute('width'));
        const imageHeight = this.image.naturalHeight || Number(this.image.getAttribute('height'));
        if (availableWidth <= 0 || availableHeight <= 0 || imageWidth <= 0 || imageHeight <= 0) return;

        const fitScale = Math.min(availableWidth / imageWidth, availableHeight / imageHeight, 1);
        const zoomScale = Math.min(fitScale * 2.5, 1);
        const scale = this.zoomed ? zoomScale : fitScale;
        this.modal.style.setProperty('--ordessa-image-width', `${Math.floor(imageWidth * scale)}px`);
        this.modal.style.setProperty('--ordessa-image-height', `${Math.floor(imageHeight * scale)}px`);
        this.button.textContent = this.zoomed ? this.button.dataset.fitImageLabel : this.button.dataset.zoomInLabel;
        this.button.disabled = !this.zoomed && zoomScale <= fitScale;

        if (resetScroll) {
          this.content.scrollLeft = 0;
          this.content.scrollTop = 0;
        }
      }

      clear() {
        this.hidden = true;
        this.image = null;
        this.zoomed = false;
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
        if (this.modal) {
          delete this.modal.dataset.ordessaImageMode;
          this.modal.style.removeProperty('--ordessa-image-width');
          this.modal.style.removeProperty('--ordessa-image-height');
        }
        if (this.button) {
          this.button.textContent = this.button.dataset.zoomInLabel;
          this.button.disabled = false;
        }
      }
    }
  );
})();
